import type { ReactNode } from "react";
import { DateInputNZ } from "@/components/ui/DateInputNZ";
import { AutofillBadge } from "./AutofillBadge";
import type { PackageFormData } from "./types";
import { FieldError } from "@/components/ui/FieldError";
import styles from "./FormField.module.css";

interface BaseProps<K extends keyof PackageFormData> {
  label: string;
  /** Field name on PackageFormData — used to read value, fire updateField,
   *  and look up autofill state. */
  field: K;
  formData: Partial<PackageFormData>;
  /** Semantic-only: forwarded to DateInputNZ's `required` attribute and the
   *  "(optional)" label suppression. No longer drives error display — that
   *  comes entirely from `error`. Default false. */
  required?: boolean;
  /** Renders the muted "(optional)" hint after the label. */
  optional?: boolean;
  showValidation?: boolean;
  autofilledFields?: Set<string>;
  /** The exact message to render for this field, looked up by the parent
   *  from the step's error map. Shown only when `showValidation` is true.
   *  Drives the red border + aria-invalid + inline alert. */
  error?: string;
  /** Append extra hint/help under the input. */
  hint?: ReactNode;
}

interface InputProps<K extends keyof PackageFormData> extends BaseProps<K> {
  as?: "input";
  type?: "text" | "date" | "number" | "email";
  inputMode?: "numeric" | "text";
  min?: string | number;
  max?: string | number;
  step?: number | string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Only meaningful for `type="date"` — forwarded to DateInputNZ so the
   *  parent can gate Continue against unparseable input even though the
   *  date input swallows invalid text. */
  onValidityChange?: (valid: boolean) => void;
}

interface TextAreaProps<K extends keyof PackageFormData> extends BaseProps<K> {
  as: "textarea";
  rows?: number;
  onChange: (value: string) => void;
}

interface SelectProps<K extends keyof PackageFormData> extends BaseProps<K> {
  as: "select";
  options: { value: string; label: string }[];
  /** Optional placeholder option — `value=""` rendered first. */
  placeholder?: string;
  onChange: (value: string) => void;
  /** Render extra UI conditional on current value (e.g. an "other" follow-up). */
  children?: ReactNode;
}

type Props<K extends keyof PackageFormData> =
  | InputProps<K>
  | TextAreaProps<K>
  | SelectProps<K>;

/**
 * Single primitive for any form field on the inquiry wizard. Owns:
 *  - label (with optional hint + autofill badge)
 *  - value defaulting (`formData[field] || ''`)
 *  - red-border + aria-invalid + inline alert driven by the `error` message
 *
 * The error message is computed once by the parent step (off its validator's
 * error map) and threaded in — FormField no longer recomputes emptiness, so
 * there is a single source of truth for "is this field valid and why not".
 *
 * Replaces ~12 hand-replicated label+input+error blocks across
 * EventDetailsStep, VenueStep. New fields drop in as one component.
 */
export function FormField<K extends keyof PackageFormData>(
  props: Props<K>,
): React.JSX.Element {
  const {
    label,
    field,
    formData,
    required = false,
    optional = false,
    showValidation = false,
    autofilledFields,
    hint,
  } = props;

  const raw = formData[field];
  const value = raw === undefined || raw === null ? "" : String(raw);
  const error = showValidation ? props.error : undefined;
  const showError = Boolean(error);
  const baseClass = `${styles.input}${showError ? ` ${styles.invalid}` : ""}`;
  // Stable id derived from the field key. Couples <label htmlFor> to the
  // rendered control so screen readers (and getByLabelText) walk from one
  // to the other; DateInputNZ is opaque and skips the linkage.
  const inputId = `inquiry-field-${String(field)}`;

  return (
    <div>
      <label
        htmlFor={inputId}
        className={styles.label}
      >
        {label}
        {optional && <span className={styles.optional}> (optional)</span>}
        {autofilledFields && (
          <AutofillBadge field={String(field)} autofilledFields={autofilledFields} />
        )}
      </label>
      {props.as === "textarea" ? (
        <textarea
          id={inputId}
          value={value}
          required={required}
          onChange={(e) => props.onChange(e.target.value)}
          rows={props.rows ?? 4}
          aria-invalid={showError || undefined}
          className={`${styles.textarea}${showError ? ` ${styles.invalid}` : ""}`}
        />
      ) : props.as === "select" ? (
        <>
          <select
            id={inputId}
            value={value}
            required={required}
            onChange={(e) => props.onChange(e.target.value)}
            aria-invalid={showError || undefined}
            className={baseClass}
          >
            {props.placeholder !== undefined && (
              <option value="">{props.placeholder}</option>
            )}
            {props.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {props.children}
        </>
      ) : props.type === "date" ? (
        <DateInputNZ
          value={value}
          min={typeof props.min === "string" ? props.min : undefined}
          hasError={showError}
          error={error}
          required={required}
          onChange={(iso) => props.onChange(iso)}
          onBlur={props.onBlur}
          onValidityChange={props.onValidityChange}
        />
      ) : (
        <input
          id={inputId}
          type={props.type ?? "text"}
          inputMode={props.inputMode}
          value={value}
          required={required}
          min={props.min}
          max={props.max}
          step={props.step}
          onChange={(e) => props.onChange(e.target.value)}
          onBlur={props.onBlur}
          aria-invalid={showError || undefined}
          className={baseClass}
        />
      )}
      {hint && <div className={styles.hint}>{hint}</div>}
      {/* DateInputNZ renders its own alert (it must also surface parseError
          text the validator can't see), so skip the duplicate here. */}
      {showError && !(props.as === undefined && props.type === "date") && (
        <FieldError message={error} />
      )}
    </div>
  );
}
